// @flow
import type { Reducer } from 'redux';

import { createReducer } from 'redux-act';

import { createSelector } from 'reselect';

import type { DurationUnit } from '@gandi/redux-resource-price';
import { selectCurrentId } from '@gandi/redux-sharing';
import { createResource } from '@gandi/resource/redux';

import type { Resource, FetchResponse } from '@gandi/resource/redux';

import type { FullState, ThunkAction } from '../../types';

type SharingId = string;

const options: {
  getListConfig: SharingId => {
    url: string,
    label: string,
    params: { sharing_id: string },
    actionExtraParams: {
      on401: (FetchResponse<>) => FetchResponse<>,
      not20X: (FetchResponse<>) => FetchResponse<> | Promise<FetchResponse<>>,
    },
  },
} = {
  getListConfig: sharingId => ({
    url: '/mail/offers',
    label: sharingId,
    params: {
      sharing_id: sharingId,
    },
    actionExtraParams: {
      // bypass the 401 error
      // to avoid natural redirect to the login screen
      // feature handled by app-runner:
      // https://gitlab.corp.gandi.net/admin-v5/gandi.react-libs/blob/cart/T104331/add-new-mailboxes/libs/app-runner/src/redux/middlewares/fetcher.js
      on401: response => response,
      not20X: response =>
        response.status === 401
          ? // bypass the 401 error
            // to avoid the infinte refetch here
            response
          : Promise.reject(response),
    },
  }),
};

export const mailboxOffersResource: Resource<typeof options> = createResource(
  'All mailbox offers',
  options,
);

export type MailboxType =
  | 'standard'
  | 'premium'
  | 'standard_new'
  | 'premium_new';
export type DurationConfig = {
  values: Array<number>,
  unit: DurationUnit,
};
export type MailboxOffer = {
  mailbox_type: MailboxType,
  /**
   * i18n string like "10GB"
   * @example "3Go" for FR
   */
  capacity: string,
  /**
   * i18n string like "premium"
   * @example
   */
  name: string,
  size: number,
  unit: string,
  sellable: boolean,
  duration: DurationConfig,
};

export type State = {
  bySharingId: {
    [label: SharingId]: Array<MailboxOffer>,
  },
};

export const fetchMailboxOffers = (
  sharingId: SharingId,
): ThunkAction<Promise<FetchResponse<200, Array<MailboxOffer>>>> => {
  const { url, label, params, actionExtraParams } =
    mailboxOffersResource.options.getListConfig(sharingId);
  // $FlowFixMe[incompatible-return]
  return mailboxOffersResource.fetch(url, {
    label,
    params,
    ...actionExtraParams,
  });
};

const initialState: State = {
  bySharingId: {},
};

export const reducer: Reducer<State, mixed> = createReducer(
  {
    [mailboxOffersResource.fetch.success.getType()]: (
      state: State,
      {
        response,
        label,
      }: {
        response:
          | FetchResponse<200, Array<MailboxOffer>>
          | FetchResponse<
              401,
              { error: string, message: string, statusCode: number },
            >,

        label: SharingId,
        ...
      },
    ): State =>
      response.status === 401
        ? // no state modification on 401 (Auth error)
          state
        : {
            ...state,
            bySharingId: {
              ...state.bySharingId,
              [label]: response.data,
            },
          },
  },
  initialState,
);

const EMPTY_ARRAY: Array<MailboxOffer> = [];
export const selectMailboxOffers = (
  state: FullState,
  label: SharingId,
): Array<MailboxOffer> => {
  const rawOffers = state.mailboxOffers.bySharingId[label];
  // handle API not well confirgured and no offers returned
  // API not well confirgured will return 200 with HTML page as result
  // no offers will set undefined
  return Array.isArray(rawOffers) ? rawOffers : EMPTY_ARRAY;
};

export const selectSellableMailboxOffers: (
  state: FullState,
  label: SharingId,
) => Array<MailboxOffer> = createSelector(selectMailboxOffers, offers =>
  offers.filter(offer => offer.sellable),
);

export const selectCanSellStandardMailbox = (state: FullState): boolean => {
  const currentId = selectCurrentId(state);
  return selectSellableMailboxOffers(state, currentId).some(
    mb => mb.mailbox_type === 'standard',
  );
};
